import type { aquaman } from '@/product-discovery/types/generated/aquaman';
import type {
  OpeningDay,
  OpeningHours,
  OpeningHoursEntry,
  SpecialOpeningHourEntry,
  StoreOpeningStatus,
} from './types';
import { DAYS_OF_WEEK } from './constants';
import { formatDate } from './date-utils';

export function createRegularOpeningHours(
  entries: aquaman.StoreInformationOpeningHours | undefined,
): OpeningHours {
  const referenceDate = new Date();
  return createOpeningHours(entries, (i) =>
    getDateInCurrentWeek(referenceDate, i),
  );
}

export function createUpcomingOpeningHours(
  entries: aquaman.StoreInformationOpeningHours | undefined,
  referenceDate = new Date(),
): OpeningHours {
  return createOpeningHours(
    entries,
    (i) => getNextOccurrenceDate(referenceDate, i),
    entries?.additionalOpeningHours ?? [],
  );
}

function createOpeningHours(
  entries: aquaman.StoreInformationOpeningHours | undefined,
  getDateForDay: (dayIndex: number) => Date,
  additionalOpeningHours: Omit<
    aquaman.StoreInformationOpeningHours['additionalOpeningHours'],
    'isClosed'
  > = [],
): OpeningHours {
  const openingHours = {} as OpeningHours;

  for (const day of DAYS_OF_WEEK) {
    const entry = entries?.[day];
    const dayIndex = DAYS_OF_WEEK.indexOf(day);

    openingHours[day] = createOpeningHourEntry({
      date: getDateForDay(dayIndex),
      entry,
      additionalOpeningHours,
    });
  }

  return openingHours;
}

export function createSpecialOpeningHours(
  entries: aquaman.StoreInformationOpeningHours | undefined,
  getFallbackLabel: (day: OpeningDay) => string = () => '',
): SpecialOpeningHourEntry[] {
  if (!entries?.additionalOpeningHours.length) {
    return [];
  }

  const result = entries.additionalOpeningHours.map((x) => {
    const calendarDate = x.date.substring(0, 10);
    const dayIndex = new Date(`${calendarDate}T00:00:00Z`).getUTCDay();
    const day = getDayFromIndex(dayIndex);

    return {
      label: x.label ?? getFallbackLabel(day),
      date: calendarDate,
      openFrom: x.openFrom ?? entries[day]?.openFrom ?? '00:00:00',
      openTo: x.openTo ?? entries[day]?.openTo ?? '00:00:00',
    } satisfies SpecialOpeningHourEntry;
  });

  return result;
}

function getDateInCurrentWeek(referenceDate: Date, dayIndex: number): Date {
  const date = new Date(referenceDate);
  date.setDate(date.getDate() + (dayIndex - referenceDate.getDay()));
  date.setHours(0, 0, 0, 0);
  return date;
}

function getNextOccurrenceDate(referenceDate: Date, dayIndex: number): Date {
  const date = new Date(referenceDate);
  date.setDate(date.getDate() + ((dayIndex - referenceDate.getDay() + 7) % 7));
  date.setHours(0, 0, 0, 0);
  return date;
}

function createOpeningHourEntry({
  date,
  entry,
  additionalOpeningHours,
}: {
  date: Date;
  entry: aquaman.StoreInformationOpeningHours[OpeningDay];
  additionalOpeningHours?: Omit<
    aquaman.StoreInformationOpeningHours['additionalOpeningHours'],
    'isClosed'
  >;
}): OpeningHoursEntry {
  const dayOfWeek = date.getDay();
  const dateString = formatDate(date);

  const specialEntry = additionalOpeningHours?.find(
    (x) => x.date.substring(0, 10) === dateString,
  );

  if (hasValidOpeningHours(specialEntry)) {
    return {
      label: specialEntry.label,
      isSpecial: true,
      date: specialEntry.date.substring(0, 10),
      openFrom: specialEntry.openFrom,
      openTo: specialEntry.openTo,
      dayOfWeek,
    };
  }

  if (hasValidOpeningHours(entry)) {
    return {
      date: dateString,
      openFrom: entry.openFrom,
      openTo: entry.openTo,
      dayOfWeek,
    };
  }

  return {
    date: dateString,
    openFrom: '00:00:00',
    openTo: '00:00:00',
    dayOfWeek,
  };
}

export function getStoreOpeningStatus(
  openingHours: OpeningHours,
  date = new Date(),
): StoreOpeningStatus {
  const dayIndex = date.getDay();
  const entry = getDayOpeningHours(openingHours, dayIndex);

  if (isOpenAt(entry, date)) {
    return {
      status: 'open',
      closesAt: entry.openTo,
    };
  }

  const next = getNextOpeningHours(openingHours, date);

  if (next == null) {
    return {
      status: 'closed',
      opensAt: '',
      opensTomorrow: false,
    };
  }

  const isOpeningLaterToday =
    next.dayOfWeek === dayIndex &&
    !isClosedAllDay(next) &&
    date < toDateTime(date, next.openFrom);

  if (isOpeningLaterToday) {
    return {
      status: 'openingSoon',
      opensAt: next.openFrom,
    };
  }

  return {
    status: 'closed',
    opensAt: next.openFrom,
    opensTomorrow: next.dayOfWeek === getDayIndex(dayIndex + 1),
    opensOnDay: DAYS_OF_WEEK[next.dayOfWeek],
  };
}

export function getNextOpeningHours(
  entries: OpeningHours,
  referenceDate = new Date(),
): OpeningHoursEntry | null {
  const currentDayIndex = referenceDate.getDay();
  const currentDayEntry = getDayOpeningHours(entries, currentDayIndex);

  // Check the current day first (early mornings)
  if (
    !isClosedAllDay(currentDayEntry) &&
    referenceDate < toDateTime(referenceDate, currentDayEntry.openFrom)
  ) {
    return currentDayEntry;
  }

  // Then check upcoming days
  for (let i = 1; i <= 7; i++) {
    const dayIndex = getDayIndex(currentDayIndex + i);
    const entry = getDayOpeningHours(entries, dayIndex);

    if (!isClosedAllDay(entry)) {
      return entry;
    }
  }

  return null;
}

function getDayOpeningHours(
  entries: OpeningHours,
  dayOfWeek: number,
): OpeningHoursEntry {
  const day = getDayFromIndex(dayOfWeek);
  return entries[day];
}

export function getDayIndex(day: number) {
  // Ensure the day value is within the range of 0-6
  return (day + 7) % 7;
}

export function isOpenAt(entry: OpeningHoursEntry, date: Date) {
  if (date.getDay() !== entry.dayOfWeek) {
    return false;
  }

  if (isClosedAllDay(entry)) {
    return false;
  }

  const openFrom = toDateTime(date, entry.openFrom);
  const openTo = toDateTime(date, entry.openTo);

  return date >= openFrom && date <= openTo;
}

export function isClosedAllDay<T extends { openFrom: string; openTo: string }>(
  entry: T,
) {
  // Equal times means the store is closed for the day (e.g. "00:00:00"/"00:00:00")
  if (entry.openFrom === entry.openTo) {
    return true;
  }

  return false;
}

function getDayFromIndex(index: number) {
  const dayIndex = getDayIndex(index);
  return DAYS_OF_WEEK[dayIndex] as OpeningDay;
}

function toDateTime(baseDate: Date, time: string) {
  const date = new Date(baseDate);
  date.setHours(0, 0, 0, 0);

  const [hours, minutes] = time.split(':').map(Number);

  if (hours != null) {
    date.setHours(hours);
  }

  if (minutes != null) {
    date.setMinutes(minutes);
  }

  return date;
}

export function hasValidOpeningHours(
  entry:
    | Omit<aquaman.StoreInformationOpeningHoursEntry, 'isOpen'>
    | null
    | undefined,
): entry is { openFrom: string; openTo: string } {
  return entry != null && entry.openFrom != null && entry.openTo != null;
}
